// Imports for external libraries go here.
import React, { FC, useReducer, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { phoenixLegalPrivacyConsentQuery } from '@marriott/mi-legal-graphql';

import { generateApolloClientHeadersForUXLCalls } from '../../utils/uxlHelper';
import { useTrackingPreferenceModal } from '../../hooks/useTrackingPreferenceModal';
import { usePreventPageExit } from '../../hooks/usePreventPageExit';
import { useAuthStore, useLegalGenericStore, usePrivacyRightsStore } from '../../store';
import {
  PrivacyRightsMainComponentActionType,
  PrivacyRightsMainComponentStateType,
} from './reducer/PrivacyRightsMainComponentReducer.types';
import {
  getPrivacyRightsMainComponentInitialState,
  privacyRightsMainComponentReducer,
} from './reducer/PrivacyRightsMainComponentReducer';
import { PrivacyRightsModal } from '../../molecules/PrivacyRightsComponent';
import { StyledPrivacyRightsMainContainer } from './PrivacyRightsWrapper.styles';

import {
  transformCustomerData,
  capitalizeFirstLetter,
  getFilteredModelDataForPrivacyRights,
  getConsentsFromCookies,
} from './utils/PrivacyRightsUtils';
import {
  PhoenixLegalPrivacyConsentResponseResult,
  PrivacyRightsMainContainerProps,
  ConsentsStatus,
  FilteredPrivacyRightsModelData,
} from './PrivacyRightsWrapper.types';
import { PrivacyRightsAlertComponent } from './PrivacyRightsAlertComponent';
import { PrivacyRightsFormSection } from './PrivacyRightsFormSection';
import { PrivacyRightsAdditionalSection } from './PrivacyRightsAdditionalSection';
import { PrivacyRightsStatementSection } from './PrivacyRightsStatementSection';
import { GET_PRIVACY_CONSENT_OPERATION_NAME, INITIAL_PRIVACY_RIGHTS_MODEL_DATA, TAB_OPTIONS } from './constant';
import { PrivacyRightsHeaderSection } from './PrivacyRightsHeaderSection';
import { PrivacyRightsLoadingSkeleton } from './PrivacyRightsLoadingSkeleton';
import { isEmpty } from '../../utils/consents';

export const PrivacyRightsMainContainer: FC<PrivacyRightsMainContainerProps> = ({ privacyRightsAemModelData }) => {
  const { isAuthenticated, sessionId } = useAuthStore();
  const { isAuthorMode, language, sessionData } = useLegalGenericStore();
  const { isFormHasPendingChange, setAlertComponentConfig, setLoggedInUserPrivacyRightsData } =
    usePrivacyRightsStore.getState();

  const [privacyRightsModelData, setPrivacyRightsModelData] = useState<FilteredPrivacyRightsModelData>(
    INITIAL_PRIVACY_RIGHTS_MODEL_DATA
  );

  const {
    trackingPreferenceSelector,
    header,
    statesLabel,
    states,
    ccpaOptInCountries,
    cpraOptInCountries,
    proceedModal: {
      btnSubmitLabel: modalProceedButtonLabel,
      btnCancelLabel: modalCancelButtonLabel,
      header: modalHeader,
      body: modalBody,
    },
    alertMessages,
    instructions,
    checkboxTitle,
    checkboxes,
    statementHeaderForGPC,
    statementTitle,
    userNamePrefix,
    additionalSection,
    form,
    cta,
    // Additional params
    subHeading,
    uniqueId,
  } = privacyRightsModelData;

  const [currentConsentsStatus, setCurrentConsentsStatus] = useState<ConsentsStatus | null>(null);
  const [consentRefreshVersion, setConsentRefreshVersion] = useState<number>(0);
  const [anyChangeInFormFields, setAnyChangeInFormFields] = useState<boolean>(false);
  const [privacyRightsMainComponentStates, dispatch] = useReducer<
    React.Reducer<PrivacyRightsMainComponentStateType, PrivacyRightsMainComponentActionType>
  >(privacyRightsMainComponentReducer, getPrivacyRightsMainComponentInitialState(TAB_OPTIONS[0]?.tabValue));

  const {
    selectedStateOrLoggedInUserStateCode,
    isUsStateAndBelongToCpraGroups,
    showFormResetConfirmationModal,
    selectedTab,
  } = privacyRightsMainComponentStates;

  // Below data will be fetched from AEM model.json
  useTrackingPreferenceModal(
    selectedStateOrLoggedInUserStateCode!,
    trackingPreferenceSelector,
    'ot-sdk-btn',
    'close-pc-btn-handler'
  );
  
  const preventPageExit = () => {
    if (isFormHasPendingChange() && anyChangeInFormFields) {
      return true;
    }
    return false;
  };

  usePreventPageExit(preventPageExit);

  const {
    data: userPrivacyConsentData,
    loading: isUserPrivacyConsentDataLoading,
    refetch: refetchUserPrivacyConsentData,
  } = useQuery<PhoenixLegalPrivacyConsentResponseResult>(phoenixLegalPrivacyConsentQuery, {
    variables: {
      customerId: sessionData?.cacheData?.data?.consumerID,
    },
    context: generateApolloClientHeadersForUXLCalls(sessionId, GET_PRIVACY_CONSENT_OPERATION_NAME, language),
    skip: isAuthorMode || !isAuthenticated,
    onError(error) {
      console.error(`Something went wrong while fetching privacyConsent details: ${JSON.stringify(error)}`);

      setAlertComponentConfig({
        alertType: 'apiError',
        showAlertComponent: true,
      });
    },
  });

  const { getNextState, setNextState } = useMemo(() => {
    let stateName: string | null;

    return {
      setNextState: (selectedState: string | null) => {
        stateName = selectedState;
      },
      getNextState: () => stateName,
    };
  }, []);

  const resetTab = () => {
    dispatch({
      type: 'SET_MULTIPLE_PRIVACY_RIGHTS_STATES',
      payload: {
        showFormResetConfirmationModal: false,
        selectedTab: TAB_OPTIONS[0].tabValue,
      },
    });
  };

  const confirmStateChange = () => {
    const updatedState: Partial<PrivacyRightsMainComponentStateType> = {
      showFormResetConfirmationModal: false,
    };

    setAlertComponentConfig({ showAlertComponent: false });

    if (selectedTab === TAB_OPTIONS[1].tabValue) {
      updatedState.isUsStateAndBelongToCpraGroups = false;
      updatedState.selectedStateOrLoggedInUserStateCode = 'NONE';
    } else {
      const nextState = getNextState();
      if (nextState) {
        updatedState.selectedStateOrLoggedInUserStateCode = nextState;
      }
    }

    dispatch({ type: 'SET_MULTIPLE_PRIVACY_RIGHTS_STATES', payload: updatedState });

    setNextState(null);
  };

  const onNewChangeInForm = (hasNewChange: boolean) => {
    setAnyChangeInFormFields(hasNewChange)
  }

  const handleStateChange = (selectedState: string) => {
    if (isFormHasPendingChange() && anyChangeInFormFields) {
      dispatch({ type: 'SET_SHOW_FORM_RESET_CONFIRMATION_MODAL', payload: true });
      setNextState(selectedState);
      return;
    }

    dispatch({ type: 'SET_SELECTED_STATE', payload: selectedState });

    setAlertComponentConfig({
      showAlertComponent: false,
    });
  };

  const handleUsNonUsResidentTabChange = (activeTabValue: string) => {
    const updates: Partial<PrivacyRightsMainComponentStateType> = {
      selectedTab: activeTabValue,
    };

    if (isFormHasPendingChange() && anyChangeInFormFields) {
      updates.showFormResetConfirmationModal = true;
    } else {
      updates.isUsStateAndBelongToCpraGroups = activeTabValue === 'us';
      updates.selectedStateOrLoggedInUserStateCode = 'NONE';
      setAlertComponentConfig({ showAlertComponent: false });
    }

    dispatch({ type: 'SET_MULTIPLE_PRIVACY_RIGHTS_STATES', payload: updates });
  };

  const refreshConsents = () => {
    setConsentRefreshVersion(prev => prev + 1);
  };

  useEffect(() => {
    if (isAuthenticated) {
      // For authenticated user get consents from customer data
      refetchUserPrivacyConsentData();
    } else {
      // For non authenticated user get consents from cookies
      const { CCPA, CPRA } = getConsentsFromCookies(ccpaOptInCountries, cpraOptInCountries);
      setCurrentConsentsStatus({
        CCPA: CCPA,
        CPRA: CPRA,
      });
    }
  }, [isAuthenticated, consentRefreshVersion]);

  useEffect(() => {
    if (!userPrivacyConsentData) {
      return;
    }

    const transformedUserData = transformCustomerData(userPrivacyConsentData);

    if (!transformedUserData?.countryCode) {
      setAlertComponentConfig({
        alertType: 'apiError',
        showAlertComponent: true,
      });
      return;
    }

    const { name, stateCode, CCPA, CPRA } = transformedUserData;

    setCurrentConsentsStatus({
      CCPA: CCPA,
      CPRA: CPRA,
    });
    const cpraStates = privacyRightsAemModelData?.states;
    const isCpraState = !isEmpty(cpraStates) && cpraStates.some(state => state.code === stateCode);

    dispatch({
      type: 'SET_MULTIPLE_PRIVACY_RIGHTS_STATES',
      payload: {
        userName: `${userNamePrefix} ${capitalizeFirstLetter(name)},`,
        isUsStateAndBelongToCpraGroups: isCpraState,
        selectedStateOrLoggedInUserStateCode: isCpraState ? (stateCode as string) : 'NONE',
      },
    });

    if (isCpraState) {
      setLoggedInUserPrivacyRightsData({ doNotSellOptIn: CCPA, doNotShareOptIn: CPRA });
    } else {
      setAlertComponentConfig({
        alertType: 'authIneligible',
        showAlertComponent: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPrivacyConsentData]);

  // Get filtered privacy rights content, checkboxes & additional section based on state change.
  useEffect(() => {
    if (!selectedStateOrLoggedInUserStateCode || !currentConsentsStatus) return;

    const filteredPrivacyRightsData = getFilteredModelDataForPrivacyRights(
      privacyRightsAemModelData || {},
      isAuthenticated,
      selectedStateOrLoggedInUserStateCode,
      currentConsentsStatus,
      isUsStateAndBelongToCpraGroups
    );

    setPrivacyRightsModelData(filteredPrivacyRightsData);
  }, [
    selectedStateOrLoggedInUserStateCode,
    isAuthenticated,
    isUsStateAndBelongToCpraGroups,
    currentConsentsStatus,
    privacyRightsAemModelData,
  ]);

  return (
    <StyledPrivacyRightsMainContainer
      data-testid="legal-PrivacyRightsWrapper"
      data-component-name="o-legal-PrivacyRightsWrapper"
    >
      <PrivacyRightsAlertComponent {...alertMessages} />

      <PrivacyRightsHeaderSection
        subHeading={subHeading}
        isAuthenticated={isAuthenticated}
        isUserPrivacyConsentDataLoading={isUserPrivacyConsentDataLoading}
        header={header}
        handleUsNonUsResidentTabChange={handleUsNonUsResidentTabChange}
        statesLabel={statesLabel}
        states={states}
        handleStateChange={handleStateChange}
        privacyRightsMainComponentStates={privacyRightsMainComponentStates}
      />

      <PrivacyRightsStatementSection
        checkboxes={checkboxes}
        statementHeaderForGPC={statementHeaderForGPC}
        statementTitle={statementTitle}
        instructions={instructions}
      />

      <PrivacyRightsFormSection
        key={`privay-rights-form-${uniqueId}`}
        formData={{
          uniqueId: uniqueId,
          email: form,
          checkboxTitle: checkboxTitle,
          checkboxes: checkboxes,
          cta: cta,
        }}
        onNewChangeInForm={onNewChangeInForm}
        privacyRightsMainComponentStates={privacyRightsMainComponentStates}
        refreshConsents={refreshConsents}
        currentConsentsStatus={currentConsentsStatus}
      />

      <PrivacyRightsAdditionalSection additionalSection={additionalSection} />

      <PrivacyRightsLoadingSkeleton isLoading={isUserPrivacyConsentDataLoading} className="privacy-rights-loader" />

      {showFormResetConfirmationModal && (
        <PrivacyRightsModal
          showModal={showFormResetConfirmationModal}
          labelledBy="exit-modal-title"
          onClose={resetTab}
          modalHeaderContent={modalHeader}
          modalBodyContent={modalBody}
          modalProceedButtonLabel={modalProceedButtonLabel}
          modalCancelButtonLabel={modalCancelButtonLabel}
          onProceed={confirmStateChange}
        />
      )}
    </StyledPrivacyRightsMainContainer>
  );
};
